/**
 * ESG SaaS Platform - Organization Service
 *
 * Handles organization and facility management.
 * Connects to FastAPI backend organization endpoints.
 */

import { api } from './api';
import { Company, BusinessUnit } from '../types';

// Request/Response types matching backend schemas
export interface OrganizationCreate {
  legal_name: string;
  trading_name?: string;
  registration_number?: string;
  headquarters_country: string;
  industry_sector: string;
  industry_subsector?: string;
  employee_count?: number;
  consolidation_approach?: string;
}

export interface OrganizationResponse {
  id: string;
  legal_name: string;
  trading_name?: string;
  headquarters_country: string;
  industry_sector: string;
  created_at: string;
  status: string;
}

export interface OrganizationListItem {
  id: string;
  legal_name: string;
  status: string;
  facilities_count: number;
  total_emissions: number;
}

export interface FacilityCreate {
  name: string;
  facility_type: string;
  address_street?: string;
  address_city?: string;
  address_country: string;
  address_postal_code?: string;
  electricity_grid: string;
  floor_area_sqm?: number;
  employee_count?: number;
  operational_status?: string;
}

export interface FacilityResponse {
  id: string;
  name: string;
  facility_type: string;
  electricity_grid: string;
  operational_status: string;
  created_at: string;
}

export interface FacilityListItem {
  id: string;
  name: string;
  type: string;
  country: string;
  status: string;
  emissions_total: number;
  electricity_grid?: string;
}

export interface FacilityDetail {
  id: string;
  organization_id: string;
  name: string;
  type: string;
  address: {
    street?: string;
    city?: string;
    country: string;
    postal_code?: string;
  };
  electricity_grid: string;
  floor_area_sqm?: number;
  employee_count?: number;
  operational_status: string;
  created_at?: string;
}

export interface DashboardMetrics {
  organization_id: string;
  period: string;
  total_emissions: {
    value: number;
    unit: string;
    trend: number;
  };
  scope_breakdown: {
    scope1: number;
    scope2: number;
    scope3: number;
  };
  data_completeness: number;
  target_progress?: {
    target: number;
    current: number;
    on_track: boolean;
  };
  alerts: Array<{ type: string; message: string }>;
}

export interface Target {
  id: string;
  name: string;
  base_year: number;
  target_year: number;
  reduction_percent: number;
  current_progress_percent: number;
  science_based: boolean;
  status: string;
}

export interface OrgUser {
  id: string;
  email: string;
  name: string;
  role: string;
  status: string;
}

// Helper to convert backend response to frontend Company type
function mapOrgToCompany(org: OrganizationResponse | OrganizationListItem): Company {
  const legalName = 'legal_name' in org ? org.legal_name : '';
  return {
    id: org.id,
    name: legalName,
    industry: 'industry_sector' in org ? org.industry_sector : '',
    headquarters: 'headquarters_country' in org ? org.headquarters_country : '',
    fiscalYearEnd: 'December',
  };
}

// Helper to convert facility to BusinessUnit
function mapFacilityToBusinessUnit(facility: FacilityListItem | FacilityDetail): BusinessUnit {
  return {
    id: facility.id,
    name: facility.name,
    location: 'country' in facility ? facility.country : facility.address?.country || '',
    type: facility.type,
  };
}

export const organizationService = {
  // ============================================================================
  // Organization CRUD
  // ============================================================================

  /**
   * Create a new organization
   */
  async createOrganization(data: OrganizationCreate): Promise<Company> {
    const response = await api.post<OrganizationResponse>('/organizations/', data);
    return mapOrgToCompany(response);
  },

  /**
   * Get organization by ID
   */
  async getOrganization(organizationId: string): Promise<Company> {
    const response = await api.get<OrganizationResponse>(`/organizations/${organizationId}`);
    return mapOrgToCompany(response);
  },

  /**
   * List all organizations
   */
  async listOrganizations(options?: {
    status?: string;
    limit?: number;
    offset?: number;
  }): Promise<{ organizations: OrganizationListItem[]; total: number }> {
    const params = new URLSearchParams();
    if (options?.status) params.append('status', options.status);
    if (options?.limit) params.append('limit', options.limit.toString());
    if (options?.offset) params.append('offset', options.offset.toString());

    const queryString = params.toString();
    const endpoint = queryString ? `/organizations/?${queryString}` : '/organizations/';

    return api.get(endpoint);
  },

  /**
   * Update organization
   */
  async updateOrganization(
    organizationId: string,
    updates: Partial<OrganizationCreate>
  ): Promise<{ id: string; updated: boolean; updated_at: string; changes: string[] }> {
    return api.put(`/organizations/${organizationId}`, updates);
  },

  /**
   * Delete organization (soft delete)
   */
  async deleteOrganization(organizationId: string): Promise<{ id: string; deleted: boolean }> {
    return api.delete(`/organizations/${organizationId}`);
  },

  // ============================================================================
  // Facility CRUD
  // ============================================================================

  /**
   * Create a new facility
   */
  async createFacility(organizationId: string, data: FacilityCreate): Promise<BusinessUnit> {
    const response = await api.post<FacilityResponse>(
      `/organizations/${organizationId}/facilities`,
      data
    );
    return {
      id: response.id,
      name: response.name,
      location: data.address_country,
      type: response.facility_type,
    };
  },

  /**
   * List facilities for an organization
   */
  async listFacilities(
    organizationId: string,
    status?: string
  ): Promise<{ facilities: FacilityListItem[]; count: number }> {
    const params = status ? `?status=${status}` : '';
    return api.get(`/organizations/${organizationId}/facilities${params}`);
  },

  /**
   * Get facility details
   */
  async getFacility(organizationId: string, facilityId: string): Promise<FacilityDetail> {
    return api.get(`/organizations/${organizationId}/facilities/${facilityId}`);
  },

  /**
   * Update facility
   */
  async updateFacility(
    organizationId: string,
    facilityId: string,
    updates: Partial<FacilityCreate>
  ): Promise<{ id: string; updated: boolean; updated_at: string; changes: string[] }> {
    return api.put(`/organizations/${organizationId}/facilities/${facilityId}`, updates);
  },

  /**
   * Delete facility (soft delete)
   */
  async deleteFacility(
    organizationId: string,
    facilityId: string
  ): Promise<{ id: string; deleted: boolean }> {
    return api.delete(`/organizations/${organizationId}/facilities/${facilityId}`);
  },

  // ============================================================================
  // User Management
  // ============================================================================

  /**
   * Create a new user for an organization
   */
  async createOrgUser(
    organizationId: string,
    data: { email: string; name: string; role?: string; facilities?: string[] }
  ): Promise<OrgUser & { organization_id: string; created_at: string }> {
    return api.post(`/organizations/${organizationId}/users`, data);
  },

  /**
   * List users for an organization
   */
  async listOrgUsers(organizationId: string): Promise<{ users: OrgUser[]; count: number }> {
    return api.get(`/organizations/${organizationId}/users`);
  },

  // ============================================================================
  // Dashboard & Metrics
  // ============================================================================

  /**
   * Get dashboard metrics for an organization
   */
  async getDashboardMetrics(organizationId: string, period: string): Promise<DashboardMetrics> {
    return api.get(`/organizations/${organizationId}/dashboard?period=${encodeURIComponent(period)}`);
  },

  /**
   * Get emission reduction targets
   */
  async getTargets(organizationId: string): Promise<{ organization_id: string; targets: Target[] }> {
    return api.get(`/organizations/${organizationId}/targets`);
  },

  /**
   * Create a new emission reduction target
   */
  async createTarget(
    organizationId: string,
    target: Omit<Target, 'id'>
  ): Promise<Target & { organization_id: string; created_at: string }> {
    return api.post(`/organizations/${organizationId}/targets`, target);
  },

  // ============================================================================
  // Helper functions for frontend mapping
  // ============================================================================

  /**
   * Convert organizations to Company array for frontend
   */
  orgListToCompanies(orgs: OrganizationListItem[]): Company[] {
    return orgs.map(mapOrgToCompany);
  },

  /**
   * Convert facilities to BusinessUnit array for frontend
   */
  facilityListToBusinessUnits(facilities: FacilityListItem[]): BusinessUnit[] {
    return facilities.map(mapFacilityToBusinessUnit);
  },
};

export default organizationService;
